/**
 * PickerSearchFilterInput — the finder's "filter by search" control.
 *
 * WHY THIS EXISTS, and it is not a duplicate of the filename box next to it:
 * the finder's text input searches the IMPORTS (filename / cached search name);
 * this one searches the SEARCHES and applies one as a filter. Before it, the
 * narrowing could only arrive from outside the picker — the "Only this search"
 * button or a shared link — and from inside the finder the only thing a
 * recruiter could do was CLEAR it. Moving from search X to search Y meant
 * clearing and then leaving the screen to re-enter through a different door.
 *
 * That button is gone (2026-08-27, Trello #729 cl.8), so this control is now the
 * only way to APPLY a narrowing without editing the URL. It stopped being a
 * convenience the day the button was removed.
 *
 * REUSES `SearchTypeahead` rather than reimplementing it. That component
 * already owns the debounce, the 1-char minimum, the `<mark>` highlight, the
 * loading / empty / error states and the id + job-title + company columns, and
 * the endpoint behind it matches the search id by PREFIX so `441` reaches 4414.
 * A second typeahead would agree with it today and drift the first time one of
 * them is taught something — the same failure the shared `id_prefix_for_query`
 * exists to prevent, one layer up.
 *
 * `value={null}` ALWAYS, plus a remount `key`, deliberately: `SearchTypeahead`
 * renders a "selected" summary with a Change button when it holds a value, and
 * that is the wrong shape here because the applied narrowing is already shown —
 * by `SearchFilterChip`, the same chip the imports listing uses. Two
 * representations of one filter is how this file's earlier state bugs started.
 * The `key` resets the input text after a pick, so the control returns to
 * "search for a search" rather than keeping the consumed query on screen.
 */

import { SearchTypeahead } from "../../../routes/research/new-import/steps/SearchTypeahead";
import type { SearchTypeaheadResult } from "../../../routes/research/new-import/steps/SearchTypeahead";
import type { ImportsClient } from "../../../clients/imports.client";

export interface PickerSearchFilterInputProps {
  client: ImportsClient;
  /** The currently applied narrowing, used only to reset the input after a pick. */
  appliedSearchId: string | null;
  /** Apply (or replace) the narrowing. The bar owns the URL — see invariant 2. */
  onApply: (searchId: string, searchName: string | null) => void;
}

export function PickerSearchFilterInput({
  client,
  appliedSearchId,
  onApply,
}: PickerSearchFilterInputProps) {
  async function search(query: string): Promise<SearchTypeaheadResult> {
    // Optional-chained for the same reason `NewImportPage` does it: a partial
    // test client without `listSearches` should render an empty typeahead
    // rather than throw.
    const res = await (client.listSearches?.(query ? { q: query } : {}) ??
      Promise.resolve({ items: [], total: 0 }));
    // Mapping copied field-for-field from `NewImportPage`'s `search`, including
    // the `?? res.items?.length ?? 0` total fallback, so the two surfaces cannot
    // render the same search differently.
    return {
      items: (res.items ?? []).map((o) => ({
        id: o.id,
        name: o.name,
        jobTitle: o.job_title ?? null,
        company: o.company ?? null,
        isTest: o.is_test,
      })),
      total: res.total ?? res.items?.length ?? 0,
    };
  }

  return (
    <div className="min-w-60 flex-1" data-testid="import-picker-search-filter">
      <SearchTypeahead
        // Remount on a change of applied narrowing so the input clears. See the
        // file docblock — this is why `value` is always null.
        key={appliedSearchId ?? "none"}
        value={null}
        valueName={null}
        onChange={(searchId, searchName) => {
          // `SearchTypeahead` calls back with (null, null) from its own Change
          // button, which this shape never renders. Guarded anyway: applying a
          // null id would write `search_id_thrive=` to the URL, which the
          // repository treats as no filter, silently widening the list from a
          // control the recruiter used to narrow it.
          if (searchId) onApply(searchId, searchName);
        }}
        search={search}
        // The finder's controls are all `--surface`; without this the input
        // rendered `--bg`, a black box beside five grey ones.
        surface="surface"
      />
    </div>
  );
}
